import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Input from '../../components/common/Input';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import authService from '../../api/services/authService';
import { handleApiError } from '../../utils/helpers';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { success, error } = useToast();
  const { dark } = useTheme();
  const d = dark;

  const phone = location.state?.phone || '';
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (!phone) navigate('/forgot-password', { replace: true }); }, [phone, navigate]);

  const getStrength = (pw) => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };
  const strengthLabels = ['','Weak','Fair','Good','Strong'];
  const strengthColors = ['','#ef4444','#f97316','#eab308','#22c55e'];
  const pwStrength = getStrength(formData.newPassword);

  const validate = () => {
    const e = {};
    if (!formData.newPassword) e.newPassword = 'New password is required';
    else if (formData.newPassword.length < 8) e.newPassword = 'Password must be at least 8 characters';
    if (!formData.confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (formData.newPassword !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await authService.resetPassword(phone, formData.newPassword);
      if (response.success) { setDone(true); success('Password reset successfully!'); }
    } catch (err) {
      error(handleApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        .lp * { font-family: 'Plus Jakarta Sans', sans-serif !important; }
        @keyframes lp-up   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lp-left { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
        @keyframes lp-blob {
          0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;transform:scale(1) translate(0,0);}
          33%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;transform:scale(1.08) translate(10px,-10px);}
          66%{border-radius:50% 60% 30% 60%/40% 30% 60% 50%;transform:scale(.95) translate(-8px,8px);}
        }
        @keyframes lp-grad  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes lp-shine { 0%{transform:translateX(-100%) skewX(-15deg)} 100%{transform:translateX(250%) skewX(-15deg)} }
        @keyframes lp-pop   { 0%{transform:scale(0);opacity:0;} 70%{transform:scale(1.15);} 100%{transform:scale(1);opacity:1;} }
        .lp .au{animation:lp-up .7s cubic-bezier(.22,1,.36,1) both;} .lp .al{animation:lp-left .7s cubic-bezier(.22,1,.36,1) both;}
        .lp .d1{animation-delay:.08s} .lp .d2{animation-delay:.16s} .lp .d3{animation-delay:.24s} .lp .d4{animation-delay:.32s}
        .lp .blob{animation:lp-blob 9s ease-in-out infinite;position:absolute;filter:blur(56px);}
        .lp .blob-2{animation-delay:-3s;} .lp .blob-3{animation-delay:-6s;}
        .lp .grad-text{background:linear-gradient(135deg,#ef4444 0%,#f97316 40%,#ef4444 70%,#dc2626 100%);background-size:300% 300%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:lp-grad 4s ease infinite;}
        .lp .btn-submit{background:linear-gradient(135deg,#ef4444,#dc2626,#b91c1c);background-size:200% 200%;animation:lp-grad 3s ease infinite;transition:transform .2s,box-shadow .2s;position:relative;overflow:hidden;}
        .lp .btn-submit:hover{transform:translateY(-2px) scale(1.02);box-shadow:0 16px 40px rgba(239,68,68,.45);}
        .lp .btn-submit:active{transform:scale(.97);}
        .lp .btn-submit::after{content:'';position:absolute;top:0;left:0;width:35%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.15),transparent);transform:translateX(-100%) skewX(-15deg);}
        .lp .btn-submit:hover::after{animation:lp-shine .5s ease forwards;}
        .lp.dark-mode input{background:rgba(255,255,255,.05)!important;border-color:rgba(255,255,255,.1)!important;color:white!important;}
        .lp.dark-mode input:focus{border-color:#ef4444!important;box-shadow:0 0 0 3px rgba(239,68,68,.15)!important;}
        .lp.dark-mode input::placeholder{color:rgba(255,255,255,.25)!important;}
        .lp.dark-mode label{color:rgba(255,255,255,.7)!important;}
        .lp.light-mode input{background:white!important;border-color:#e5e7eb!important;color:#111827!important;}
        .lp.light-mode input:focus{border-color:#ef4444!important;box-shadow:0 0 0 3px rgba(239,68,68,.1)!important;}
        .lp.light-mode label{color:#374151!important;}
        .lp .grid-bg-dark{background-image:linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px);background-size:48px 48px;}
        .lp .grid-bg-light{background-image:linear-gradient(rgba(0,0,0,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.04) 1px,transparent 1px);background-size:48px 48px;}
        .lp .success-icon{animation:lp-pop .5s cubic-bezier(.22,1,.36,1) both;}
        .lp .strength-bar{transition:width .4s cubic-bezier(.22,1,.36,1),background .4s;}
      `}</style>

      <Navbar />

      <div className={`lp ${d ? 'dark-mode' : 'light-mode'} min-h-screen flex pt-20 transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-gray-50'}`}>

        {/* LEFT PANEL */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <div className="absolute inset-0" style={{ background: d ? 'linear-gradient(135deg,#030712 0%,#1a0000 40%,#030712 100%)' : 'linear-gradient(135deg,#fff1f2 0%,#ffe4e6 40%,#fff1f2 100%)' }}/>
          <div className={`absolute inset-0 ${d ? 'grid-bg-dark' : 'grid-bg-light'}`}/>
          <div className="blob blob-1 w-80 h-80 top-16 left-10 opacity-30" style={{ background: d?'#ef4444':'#fca5a5' }}/>
          <div className="blob blob-2 w-72 h-72 top-48 right-8 opacity-20"  style={{ background: d?'#fff':'#fed7aa' }}/>
          <div className="blob blob-3 w-64 h-64 bottom-20 left-32 opacity-25" style={{ background: d?'#dc2626':'#fca5a5' }}/>
          <div className="relative z-10 flex flex-col justify-center px-14 py-16">
            <div className="al d1 mb-3">
              <span className={`text-xs font-black tracking-widest uppercase px-3 py-1 rounded-full border ${d ? 'text-red-400 bg-red-500/10 border-red-500/20' : 'text-red-600 bg-red-50 border-red-200'}`}>Final Step</span>
            </div>
            <h1 className={`al d2 text-5xl font-black leading-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Set a new<br/><span className="grad-text">password</span>
            </h1>
            <p className={`al d3 text-base mb-12 leading-relaxed max-w-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              Choose a strong password to keep your account secure.
            </p>
            <div className={`al d4 rounded-2xl p-5 border ${d ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-md'}`}>
              <p className={`text-sm font-bold mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>🔐 Password requirements</p>
              {[
                { label:'At least 8 characters',   met: formData.newPassword.length >= 8 },
                { label:'One uppercase letter',     met: /[A-Z]/.test(formData.newPassword) },
                { label:'One number',               met: /[0-9]/.test(formData.newPassword) },
                { label:'One special character',    met: /[^A-Za-z0-9]/.test(formData.newPassword) },
              ].map((req, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${req.met ? 'bg-green-500' : d ? 'bg-white/10' : 'bg-gray-200'}`}>
                    {req.met && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                  </div>
                  <span className={`text-xs ${req.met ? 'text-green-500 font-semibold' : d ? 'text-gray-400' : 'text-gray-500'}`}>{req.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className={`w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 lg:p-14 relative ${d ? 'bg-gray-950' : 'bg-white'}`}>
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none" style={{ background:'radial-gradient(circle,#ef4444,transparent)' }}/>
          <div className="w-full max-w-md relative z-10">

            {done ? (
              <div className="text-center">
                <div className="success-icon w-24 h-24 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                </div>
                <h2 className={`text-3xl font-black mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>Password Reset!</h2>
                <p className={`text-sm mb-8 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Your password has been reset successfully. You can now log in with your new password.</p>
                <button onClick={() => navigate('/login')}
                  className="btn-submit w-full text-white font-black text-base py-3.5 rounded-xl flex items-center justify-center gap-2">
                  Go to Login
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                </button>
              </div>
            ) : (
              <>
                {/* Step indicator */}
                <div className="au d1 flex items-center gap-2 justify-center mb-6">
                  {[1,2,3].map(n => (
                    <div key={n} className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border-2 bg-red-500 border-red-500 text-white`}>
                        {n < 3 ? '✓' : n}
                      </div>
                      {n < 3 && <div className="w-8 h-0.5 bg-red-500"/>}
                    </div>
                  ))}
                </div>

                <div className="au d1 text-center mb-8">
                  <h2 className={`text-3xl font-black mb-1.5 ${d ? 'text-white' : 'text-gray-900'}`}>New Password</h2>
                  <p className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>Step 3 of 3 — Set your new password</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className={`au d2 rounded-2xl p-6 border transition-all duration-300 ${d ? 'bg-gray-900/80 border-white/8 hover:border-red-500/30' : 'bg-gray-50 border-gray-200 hover:border-red-300 shadow-sm'}`}>
                    <div className="space-y-4">
                      <Input label="New Password" type="password" name="newPassword" value={formData.newPassword} onChange={handleChange} placeholder="••••••••" error={errors.newPassword} required />
                      {formData.newPassword && (
                        <div>
                          <div className={`h-1.5 rounded-full overflow-hidden ${d ? 'bg-white/10' : 'bg-gray-200'}`}>
                            <div className="h-full rounded-full strength-bar" style={{ width:`${(pwStrength/4)*100}%`, background:strengthColors[pwStrength] }}/>
                          </div>
                          <p className="text-xs mt-1 font-semibold" style={{ color:strengthColors[pwStrength] }}>{strengthLabels[pwStrength]}</p>
                        </div>
                      )}
                      <Input label="Confirm New Password" type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••" error={errors.confirmPassword} required />
                    </div>
                  </div>

                  <button type="submit" disabled={loading}
                    className="au d3 btn-submit w-full text-white font-black text-base py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                    {loading ? (
                      <><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>Resetting...</>
                    ) : (
                      <>Reset Password<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg></>
                    )}
                  </button>

                  <p className={`au d4 text-center text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Link to="/login" className="text-red-500 hover:text-red-400 font-bold transition-colors">← Back to login</Link>
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;